import React, { useState, useEffect } from 'react';
import { Play, ExternalLink, Youtube } from 'lucide-react';
import '../styles/YouTubeEmbed.css';

interface YouTubeEmbedProps {
  url: string;
  title?: string;
  className?: string;
  preview?: 'auto' | 'thumbnail' | 'card' | 'minimal';
  autoplay?: boolean;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ url, title, className = '', preview = 'thumbnail', autoplay = true }) => {
  const [showEmbed, setShowEmbed] = useState(preview === 'auto');
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  
  const handlePlayClick = () => {
    if (preview === 'thumbnail' || preview === 'card') {
      setShowEmbed(true);
    } else {
      // For minimal preview, open directly in YouTube
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  // Function to extract video ID from various YouTube URL formats
  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/.*[?&]v=)([^&#\n?]*)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Check if URL contains YouTube
  const isYouTubeUrl = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const videoId = extractVideoId(url);

  // Function to get the best available thumbnail
  const getBestThumbnail = async (videoId: string): Promise<string> => {
    const thumbnailOptions = [
      `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`, // 1280x720
      `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,     // 480x360
      `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,     // 320x180
      `https://img.youtube.com/vi/${videoId}/default.jpg`        // 120x90
    ];

    for (const thumbnailUrl of thumbnailOptions) {
      try {
        const response = await fetch(thumbnailUrl, { method: 'HEAD' });
        if (response.ok && response.headers.get('content-length') !== '0') {
          return thumbnailUrl;
        }
      } catch (error) {
        continue;
      }
    }
    
    // Fallback to default if all fail
    return thumbnailOptions[thumbnailOptions.length - 1];
  };

  useEffect(() => {
    if (videoId) {
      getBestThumbnail(videoId).then(setThumbnailUrl);
    }
  }, [videoId]);

  // If not a YouTube URL or can't extract video ID, return null
  if (!isYouTubeUrl(url) || !videoId) {
    return null;
  }

  const videoTitle = title || 'YouTube Video';

  // Auto embed (original behavior)
  if (preview === 'auto' || showEmbed) {
    return (
      <div className={`youtube-embed ${className}`}>
        <div className="youtube-wrapper">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}${autoplay ? '?autoplay=1' : ''}`}
            title={videoTitle}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  // Minimal preview (just a styled link)
  if (preview === 'minimal') {
    return (
      <div className={`youtube-minimal ${className}`}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="youtube-minimal-link">
          <Play size={20} />
          <span>{videoTitle}</span>
          <ExternalLink size={16} />
        </a>
      </div>
    );
  }

  // Card preview
  if (preview === 'card') {
    return (
      <div className={`youtube-card ${className}`}>
        <div className="youtube-card-content">
          <div className="youtube-card-thumbnail" onClick={handlePlayClick}>
            {thumbnailUrl ? (
              <>
                <img src={thumbnailUrl} alt={videoTitle} />
                <button 
                  className="youtube-play-overlay"
                  onClick={handlePlayClick}
                  aria-label="Play video"
                >
                  <Play size={32} />
                </button>
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="youtube-external-link"
                  title="Open in YouTube"
                  aria-label="Open in YouTube"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={20} />
                </a>
              </>
            ) : (
              <div className="youtube-thumbnail-loading">
                <Play size={32} />
                <p>Loading...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show loading or fallback while thumbnail loads
  if (!thumbnailUrl) {
    return (
      <div className={`youtube-thumbnail ${className}`}>
        <div className="youtube-thumbnail-container">
          <div className="youtube-thumbnail-loading">
            <Play size={32} />
            <p>Loading video preview...</p>
          </div>
        </div>
      </div>
    );
  }

  // Thumbnail preview (default)
  return (
    <div className={`youtube-thumbnail ${className}`}>
      <div className="youtube-thumbnail-container" onClick={handlePlayClick}>
        <img src={thumbnailUrl} alt={videoTitle} />
        <button 
          className="youtube-play-overlay"
          onClick={handlePlayClick}
          aria-label={`Play ${videoTitle}`}
        >
          <Play size={32} />
        </button>
        <div className="youtube-thumbnail-title">
          {videoTitle}
        </div>
      </div>
    </div>
  );
};

export default YouTubeEmbed;